'use client'

import { Facebook, Instagram, Linkedin, MessageCircle } from 'lucide-react'
import Link from 'next/link'
import { Logo } from '@/components/Logo/Logo'
import type { Footer as FooterType } from '@/payload-types'
import { useTheme } from '@/providers/Theme'
import { FaTiktok } from "react-icons/fa";

const socialIcons = {
  facebook: Facebook,
  instagram: Instagram,
  linkedin: Linkedin,
  whatsapp: MessageCircle,
  tiktok: FaTiktok,
}

interface FooterClientProps {
  footer: FooterType
  lang?: 'en' | 'fr' | 'ar' | 'all' | undefined
}

export function FooterClient({ footer, lang }: FooterClientProps) {
  const { theme } = useTheme()

  const tagline = footer?.tagline || 'connecting agencies with buyers and renters in Algeria.'
  const socialLinks = footer?.socialLinks || []
  const followUs = footer?.followUs || 'Follow us'
  const navigationLinks = footer?.navigationLinks || []
  const contactButtonText = footer?.contactButtonText || 'Contact Us'
  const contactButtonUrl = footer?.contactButtonUrl || '/contact'
  const copyright = footer?.copyright || '© 2025 All Rights Reserved'

  return (
    <footer className="bg-primary text-white">
      <div className="container mx-auto px-6">
        {/* Main Footer Content */}
        <div className="py-8 lg:py-12">
          {/* Mobile Layout */}
          <div className="flex flex-col lg:hidden">
            {/* Logo and tagline */}
            <div className="flex justify-center items-center mb-6">
              <div className="flex flex-col items-center">
                <Logo className="h-10 w-auto px-4 mb-2" priority="high" loading="eager" lang={lang} />
                <p className="text-gray-200 text-sm max-w-xs leading-relaxed text-center">{tagline}</p>
              </div>
            </div>

            {/* Follow us and social media */}
            <div className="flex flex-col items-center mb-6">
              <span className="text-gray-200 text-sm font-medium mb-3">{followUs}</span>
              <div className="flex items-center space-x-4">
                {socialLinks.map(({ platform, url }) => {
                  const Icon = socialIcons[platform]
                  return (
                    <a
                      key={platform}
                      href={url}
                      className="w-8 h-8 bg-white border border-white hover:bg-gray-100 rounded-full flex items-center justify-center transition-colors"
                      aria-label={platform}
                    >
                      <Icon size={16} className="text-black" />
                    </a>
                  )
                })}
              </div>
            </div>

            {/* Contact button */}
            <div className="flex justify-center">
              <Link
                href={contactButtonUrl}
                className="bg-white text-black px-6 py-2 rounded-full font-medium hover:bg-gray-100 transition-colors text-sm border-black"
              >
                {contactButtonText}
              </Link>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="relative hidden lg:flex items-center">
            {/* Left Section - Logo and Tagline */}
            <div className="flex items-center">
              <div className="flex flex-col">
                <Logo className="h-10 w-auto px-4 mb-2"  priority="high" loading="eager" lang={lang} />
                <p className="text-gray-200 text-sm max-w-xs leading-relaxed">{tagline}</p>
              </div>
            </div>

            {/* Center Section - Social Media and Navigation - Absolutely centered */}
            <div className="absolute left-1/2 transform -translate-x-1/2 flex flex-col items-center">
              {/* Follow us text */}
              <span className="text-gray-200 text-sm font-medium mb-3">{followUs}</span>

              {/* Social Media Icons */}
              <div className="flex justify-center items-center space-x-4 mb-6">
                {socialLinks.map(({ platform, url }) => {
                  const Icon = socialIcons[platform]
                  return (
                    <a
                      key={platform}
                      href={url}
                      className="w-8 h-8 bg-white border border-white hover:bg-gray-100 rounded-full flex items-center justify-center transition-colors !mx-3"
                      aria-label={platform}
                    >
                      <Icon size={16} className="text-black" />
                    </a>
                  )
                })}
              </div>

              {/* Navigation (Desktop) */}
              <nav className="flex justify-center items-center space-x-8">
                {navigationLinks.map(({ label, url }) => (
                  <Link
                    key={label}
                    href={url}
                    className="text-gray-200 hover:text-white transition-colors text-sm font-medium !mx-6"
                  >
                    {label}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Right Section - Contact Button */}
            <div className="rtl:mr-auto ltr:ml-auto">
              <Link
                href={contactButtonUrl}
                className="bg-white text-black px-6 py-2 rounded-full font-medium hover:bg-gray-100 transition-colors text-sm border-black"
              >
                {contactButtonText}
              </Link>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <nav className="lg:hidden mb-6">
          <div className="flex justify-center gap-8">
            {navigationLinks.map(({ label, url }) => (
              <Link
                key={label}
                href={url}
                className="text-gray-200 hover:text-white transition-colors text-sm font-medium text-center"
              >
                {label}
              </Link>
            ))}
          </div>
        </nav>

        {/* Copyright */}
        <div className="border-t border-white/10 pt-6 pb-6">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center">
            {/* Empty space for left alignment on desktop */}
            <div className="hidden lg:block flex-1"></div>

            {/* Centered copyright */}
            <div className="flex justify-center flex-1">
              <p className="text-gray-300 text-sm">{copyright}</p>
            </div>

            {/* Empty space for right alignment on desktop */}
            <div className="hidden lg:block flex-1"></div>
          </div>
        </div>
      </div>
    </footer>
  )
}
